import React, { useState, useEffect } from "react"

import Image from "next/image"
import Button from "components/Buttons/Button"
import {
	Box,
	FormControl,
	InputBase,
	InputAdornment,
	IconButton,
	Grid,
	InputLabel,
	Select,
	MenuItem,
	Checkbox,
	FormControlLabel,
	List,
	ListItem,
	ListItemIcon,
	ListItemText,
	Typography,
	TextField,
} from "@mui/material"
import { makeStyles } from "makeStyles"
import TextInput from "components/Forms/TextInput"
import ToggleButton from "@mui/material/ToggleButton"
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup"
import { AiOutlineSearch } from "react-icons/ai"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"

import axios from "axios"
import { Response } from "pages/api/searchProductProjections"
import { Response as GetRegisterProductFileUploadUrlResponse } from "pages/api/getRegisterProductFileUploadUrl"
import { RequestBody } from "pages/api/register-product"
import useDebounce from "@secureo/common/hooks/useDebounce"
import useInputValue from "@secureo/common/hooks/useInputValue"
import useToggle from "@secureo/common/hooks/useToggle"
import FileUpload from "./FileUpload"

import { AiOutlineCloseCircle } from "react-icons/ai"

const useStyles = makeStyles()(() => ({
	textField: {
		width: "100%",
		background: "none",
		borderRadius: 5,
		border: "1px solid whitesmoke",
		padding: "10px 0px 10px 10px",
		marginTop: 5,
	},
	inputStyle: {
		color: "#67767B !important",
		zIndex: 0,
		opacity: 1,
		fontSize: "14px",
	},
	searchBox: {
		background: "#fff",
		maxHeight: 260,
		overflowY: "scroll",
		position: "absolute",
		width: "97%",
		zIndex: 4,
		borderRadius: 3,
		borderTopLeftRadius: 0,
		borderTopRightRadius: 0,
		marginTop: -5,
		border: "1px solid rgba(0,0,0,0.05)",
		boxShadow: " 0 10px 8px rgba(0, 0, 0, 0.07)",
	},
	responsiveBox: {
		color: "black",
		padding: "30px",
		"@media only screen and (max-width: 960px)": {
			maxHeight: "auto",
			overflowX: "hidden",
		},
	},
	responsiveButton: {
		maxWidth: "250px",
		float: "right",
		"@media only screen and (max-width: 960px)": {
			maxWidth: "100%",
			float: "right",
		},
	},
}))

const useProductSearch = (query: string) => {
	const [results, setResults] = useState([] as Response)

	useEffect(() => {
		const fetchAndSetResults = async () => {
			if (query.length > 0) {
				const { data: results } = (await axios.get(
					`/api/searchProductProjections?query=${query}`,
				)) as { data: Response }

				setResults(results.filter((result) => result.variants.length > 1))
			}
		}

		if (query.length > 0) {
			fetchAndSetResults()
		}
	}, [query])

	return results
}

const Form = () => {
	const { classes } = useStyles()

	const query = useInputValue("")
	const debouncedQuery = useDebounce(query.value, 700)
	const results = useProductSearch(debouncedQuery)

	// @lnrfl: use this state to select a product
	const [product, setProduct] = useState(null)
	const [lock, setLock] = useState(null as string)

	const locks =
		product?.variants
			?.map((variant) => {
				const { attributes } = variant
				const lockType = attributes?.find((attribute) => attribute.name === "lockType")
					?.value

				return lockType
			})
			?.filter(Boolean) ?? []

	const [file, setFile] = useState(null as File)
	const [isLoading, setIsLoading] = useState(false)
	const [isSuccess, setIsSuccess] = useState(false)

	const shop = useInputValue("TRE")

	const firstName = useInputValue("")
	const lastName = useInputValue("")
	const email = useInputValue("")
	const phone = useInputValue("")
	const salutation = useInputValue("Mr")
	const [date, setDate] = useState(new Date())

	const [hasAcceptedDataPrivacyPolicy, toggleHasAcceptedDataPrivacyPolicy] = useToggle(false)
	const [hasAcceptedNewsletterSubscription, toggleHasAcceptedNewsletterSubscription] =
		useToggle(false)

	const onFilesRead = (files: File[]) => {
		setFile(files[0])
	}

	const onSubmit = async (e) => {
		e.preventDefault()

		try {
			setIsLoading(true)
			const { type, name } = file
			const fileExtension = name.split(".").reverse()[0]
			const {
				data: { uuid, uploadUrl },
			} = (await axios.get(
				`/api/getRegisterProductFileUploadUrl?fileType=${type}&fileExtension=${fileExtension}`,
			)) as {
				data: GetRegisterProductFileUploadUrlResponse
			}

			await axios.put(uploadUrl, file, {
				headers: {
					"Content-Type": file.type,
				},
			})

			const fileName = `${uuid}.${fileExtension}`

			const requestBody: RequestBody = {
				productSku: product?.sku,
				lockType: lock,
				purchaseDate: date.toLocaleDateString(),
				shopKey: shop.value as any,
				customer: {
					salutation: salutation.value as any,
					firstName: firstName.value,
					lastName: lastName.value,
					email: email.value,
					phone: phone.value,
				},
				subscribeToNewsLetter: hasAcceptedNewsletterSubscription,
				imageFileName: fileName,
				uuid,
			}

			await axios.post("/api/register-product", requestBody)
			setIsSuccess(true)
		} catch (err) {
			console.error(err)
		} finally {
			setIsLoading(false)
		}
	}

	return (
		<div className={classes.responsiveBox}>
			<form onSubmit={onSubmit}>
				<div>
					<strong>Produkt auswählen</strong>
				</div>
				<div style={{ position: "relative" }}>
					<Box mt={1} style={{ display: "flex", alignItems: "center" }}>
						<FormControl className={classes.textField} variant="filled">
							<InputBase
								id="search"
								{...query}
								startAdornment={
									<InputAdornment position="start">
										<IconButton
											style={{ padding: 5, marginTop: -15 }}
											size="large"
										>
											<AiOutlineSearch size="1.5rem" color="#C6CCCE" />
										</IconButton>
									</InputAdornment>
								}
								// onChange={onChange}
								// value={searchQuery}
								inputProps={{
									"data-hj-whitelist": true,
									"data-hj-allow": true,
								}}
								placeholder="Produktname, SKU oder Tresortyp"
								className={classes.inputStyle}
							/>
						</FormControl>
					</Box>
					{debouncedQuery.length > 2 && (
						<Box p={1} className={classes.searchBox}>
							<List>
								{results.map((result) => {
									const {
										name,
										id,
										masterVariant: { images },
									} = result

									return (
										<ListItem
											key={id}
											button
											onClick={() => {
												setProduct(result)
												query.onChange({ target: { value: "" } } as any)
											}}
										>
											<ListItemIcon>
												{images[0].url && (
													<Image
														alt={name.de}
														src={images[0].url}
														width={35}
														height={35}
														style={{
															maxWidth: "100%",
															height: "auto",
															objectFit: "contain",
														}}
													/>
												)}
											</ListItemIcon>
											<ListItemText primary={name.de} />
										</ListItem>
									)
								})}
							</List>
						</Box>
					)}
				</div>
				{product && (
					<Box
						mt={1}
						p={1}
						style={{
							background: "rgba(0, 156, 172, 0.1)",
							borderRadius: 5,
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
						}}
					>
						<div style={{ display: "flex", alignItems: "center" }}>
							<Box mr={2} mt={1}>
								<Image
									alt={product.name.de}
									src={product?.masterVariant?.images[0].url}
									width={60}
									height={60}
									style={{
										maxWidth: "100%",
										height: "auto",
										objectFit: "contain",
									}}
								/>
							</Box>
							<div>
								<span style={{ fontSize: 18 }}>{product?.name.de}</span> <br />
								<span style={{ fontSize: 13 }}>
									SKU: {product?.masterVariant?.sku}
								</span>
							</div>
						</div>
						<IconButton
							onClick={() => {
								setProduct(null)
								setLock(null)
							}}
							style={{ height: 50, width: 50 }}
							size="large"
						>
							<AiOutlineCloseCircle size="1.2rem" />
						</IconButton>
					</Box>
				)}
				{locks.length > 0 && (
					<Box
						mt={1}
						p={1}
						style={{ background: "rgba(0, 156, 172, 0.1)", borderRadius: 5 }}
					>
						<List>
							<ListItem>
								<Typography variant="h6" style={{ color: "#0098AA" }}>
									Schloss auswählen
								</Typography>
							</ListItem>
							{locks.map((lockType) => {
								return (
									<ListItem
										key={lockType}
										button
										onClick={() => {
											setLock(lockType)
										}}
									>
										<ListItemIcon>
											{lockType}{" "}
											{lock === lockType && <span>&nbsp;(Ausgewählt)</span>}
										</ListItemIcon>
									</ListItem>
								)
							})}
						</List>
					</Box>
				)}

				<Box mt={4}>
					<strong>Kaufdetails</strong>
				</Box>
				<Box mb={4}>
					Rechnung hochladen
					{file ? (
						<p>{file.name}</p>
					) : (
						<FileUpload
							onFilesRead={onFilesRead}
							accept={{ "image/*": [".png", ".gif", ".jpeg", ".jpg"] }}
							disabled={isLoading}
						/>
					)}
				</Box>
				<Grid container spacing={3}>
					<Grid item xs={12} sm={6}>
						<DatePicker
							label="Kaufdatum"
							value={date}
							onChange={setDate}
							disableFuture
							slots={{
								textField: (props) => <TextField {...props} />,
							}}
						/>
					</Grid>
					<Grid item xs={12} sm={6}>
						<FormControl
							variant="outlined"
							style={{ width: "100%" }}
							disabled={isLoading}
						>
							<InputLabel id="select-shop">Shop auswählen</InputLabel>
							<Select labelId="select-shop" id="select-shop" {...shop} label="Shop">
								<MenuItem value={"TRE"}>Tresoro</MenuItem>
								<MenuItem value={"DZN"}>Digitalzylinder-Shop</MenuItem>
								<MenuItem value={"SH"}>SafeHero</MenuItem>
							</Select>
						</FormControl>
					</Grid>
				</Grid>

				<Box mt={4} mb={2}>
					<strong>Kundendaten angeben</strong>
				</Box>
				<ToggleButtonGroup {...salutation} exclusive aria-label="text alignment">
					<ToggleButton value="Mr" aria-label="left aligned" disabled={isLoading}>
						Herr
					</ToggleButton>
					<ToggleButton value="Mrs" aria-label="centered" disabled={isLoading}>
						Frau
					</ToggleButton>
				</ToggleButtonGroup>
				<Grid container spacing={2} style={{ marginTop: 15 }}>
					<Grid item xs={12} sm={6}>
						<TextInput
							label={"Vorname"}
							name={"firstName"}
							id={"firstName"}
							{...firstName}
							required
							fullWidth
							variant="outlined"
							disabled={isLoading}
							style={{ marginBottom: -10, borderRadius: 0 }}
						/>
					</Grid>
					<Grid item xs={12} sm={6}>
						<TextInput
							label={"Nachname"}
							name={"lastName"}
							id={"lastName"}
							{...lastName}
							required
							fullWidth
							variant="outlined"
							disabled={isLoading}
							style={{ marginBottom: -10 }}
						/>
					</Grid>
					<Grid item xs={12} sm={6}>
						<TextInput
							label={"E-Mail"}
							name={"email"}
							id={"email"}
							{...email}
							type="email"
							required
							fullWidth
							variant="outlined"
							disabled={isLoading}
							style={{ marginBottom: -10 }}
						/>
					</Grid>
					<Grid item xs={12} sm={6}>
						<TextInput
							label={"Telefonnummer"}
							name={"phone"}
							id={"phone"}
							type="phone"
							{...phone}
							required
							fullWidth
							variant="outlined"
							disabled={isLoading}
							style={{ marginBottom: -10 }}
						/>
					</Grid>
					<Grid item xs={12} style={{ marginTop: 15 }}>
						<FormControlLabel
							control={
								<Checkbox
									name="accept-policies"
									checked={hasAcceptedDataPrivacyPolicy}
									onClick={toggleHasAcceptedDataPrivacyPolicy}
									disabled={isLoading}
									required
								/>
							}
							label={
								<div style={{ fontSize: 13, opacity: 0.75 }}>
									Ich bin mit den{" "}
									<a target="_blank" href="/">
										Datenschutzrichtlinien
									</a>{" "}
									und den
									<a target="_blank" href="/">
										Nutzungsbedingungen
									</a>{" "}
									einverstanden.
								</div>
							}
						/>
						<Box mt={1}>
							<FormControlLabel
								control={
									<Checkbox
										name="newsletter"
										checked={hasAcceptedNewsletterSubscription}
										onClick={toggleHasAcceptedNewsletterSubscription}
										disabled={isLoading}
									/>
								}
								label={
									<div style={{ fontSize: 13, opacity: 0.75 }}>
										Ich möchte keine Updates zu neuesten Produktinformationen
										und Angeboten erhalten.
									</div>
								}
							/>
						</Box>
						<Box my={3} className={classes.responsiveButton}>
							<Button
								color="primary"
								type="submit"
								disabled={!product || (locks.length > 0 && !lock)}
							>
								Produkt registrieren
							</Button>
						</Box>
					</Grid>
				</Grid>
			</form>
		</div>
	)
}

export default Form
